import type {Catalog} from './catalog';
import type {Character} from './character';
import {choiceLimit} from './choice-limits';
import {choiceOptions} from './choice-options';
import type {CharacterSpell} from './collect-effects';
import type {ChoiceInstance} from './feature-tree';
import {isPreparedOnSheet} from './spell-preparation';

export type SpellPreparation = {
	choice: ChoiceInstance;
	/** сколько можно подготовить; без предела — undefined */
	limit?: number;
	/** из чего можно выбрать: весь список класса или книга заклинаний */
	candidates: string[];
};

export type SpellListEntry = {
	spell: CharacterSpell;
	/** подготовка, в которую входит заклинание: отметка в списке меняет её */
	preparation?: SpellPreparation;
	/** класс, у которого заклинание уже подготовлено: второй раз его подготовить нельзя */
	takenByClassId?: string;
};

export type SpellManagementTab = {
	key: string;
	/** класс вкладки; без него — заклинания черт, вида и предметов */
	classId?: string;
	preparation?: SpellPreparation;
	entries: SpellListEntry[];
};

export function spellPreparations(catalog: Catalog, character: Character): SpellPreparation[] {
	return character.choices
		.filter((choice) => isPreparedOnSheet(choice, character.getFeatureInstance(choice.featureKey)?.feature))
		.map((choice) => ({
			choice,
			limit: choiceLimit(choice, character),
			candidates: choiceOptions(choice, catalog, character).map((option) => option.key),
		}));
}

export function spellListEntries(preparations: SpellPreparation[], character: Character): SpellListEntry[] {
	const preparationOf = (spellId: string) =>
		preparations.find(({choice}) => choice.selected.includes(spellId)) ||
		preparations.find(({candidates}) => candidates.includes(spellId));

	const knownEntries = character.knownSpells.map((spell) => ({
		spell,
		preparation: spell.needsPreparation ? preparationOf(spell.id) : undefined,
	}));
	const knownIds = new Set(character.knownSpells.map((spell) => spell.id));
	const candidateEntries = preparations.flatMap((preparation) =>
		preparation.candidates
			.filter((spellId) => !knownIds.has(spellId))
			.map((spellId) => ({
				spell: candidateSpell(spellId, preparation),
				preparation,
			})),
	);
	return [...knownEntries, ...candidateEntries];
}

const candidateSpell = (spellId: string, preparation: SpellPreparation): CharacterSpell => ({
	id: spellId,
	prepared: false,
	alwaysPrepared: false,
	needsPreparation: true,
	classIds: preparation.choice.owner.classId ? [preparation.choice.owner.classId] : [],
	castings: [{key: `slot:${spellId}`, kind: 'slot'}],
});

export function spellManagementTabs(preparations: SpellPreparation[], character: Character): SpellManagementTab[] {
	const takenBy = (spellId: string, preparation: SpellPreparation | undefined) => {
		const other = preparations.find(
			(candidate) => candidate !== preparation && candidate.choice.selected.includes(spellId),
		);
		if (other) return other.choice.owner.classId;
		const known = character.knownSpells.find((spell) => spell.id === spellId);
		if (!known?.alwaysPrepared) return undefined;
		return known.classIds.find((classId) => classId !== preparation?.choice.owner.classId) || known.classIds[0];
	};

	const classTabs = character.classes.map(({classData}): SpellManagementTab => {
		const classId = classData.id;
		const preparation = preparations.find(({choice}) => choice.owner.classId === classId);
		const known = character.knownSpells.filter((spell) => spell.classIds.includes(classId));
		const knownIds = new Set(known.map((spell) => spell.id));
		const inPreparation = (spellId: string) =>
			!!preparation &&
			(preparation.choice.selected.includes(spellId) ||
				preparation.candidates.includes(spellId) ||
				!!takenBy(spellId, preparation));
		const withTaken = (entry: SpellListEntry): SpellListEntry =>
			entry.preparation &&
			!entry.spell.alwaysPrepared &&
			!entry.preparation.choice.selected.includes(entry.spell.id)
				? {...entry, takenByClassId: takenBy(entry.spell.id, entry.preparation)}
				: entry;
		return {
			key: classId,
			classId,
			preparation,
			entries: [
				...known.map((spell) =>
					withTaken({
						spell,
						preparation: spell.needsPreparation && inPreparation(spell.id) ? preparation : undefined,
					}),
				),
				...(preparation?.candidates || [])
					.filter((spellId) => !knownIds.has(spellId))
					.map((spellId) => withTaken({spell: candidateSpell(spellId, preparation!), preparation})),
			],
		};
	});
	const otherTab: SpellManagementTab = {
		key: 'other',
		entries: character.knownSpells.filter((spell) => !spell.classIds.length).map((spell) => ({spell})),
	};
	return [...classTabs, otherTab].filter((tab) => tab.entries.length);
}

export const isSpellReady = ({spell, preparation}: SpellListEntry) =>
	!preparation || spell.alwaysPrepared || preparation.choice.selected.includes(spell.id);
